import React, { useState } from 'react';
import { Shield, Store, Check, X, Save } from 'lucide-react';
import styles from './AccessControl.module.css';

// Mock Data for Roles & Modules
const roles = ['Manager', 'Receptionist', 'Senior Stylist', 'Junior Staff'];
const availableStores = [
  { id: 's1', name: 'Smiify CP Branch' },
  { id: 's2', name: 'Smiify Noida Sector 18' },
  { id: 's3', name: 'Smiify Gurugram' }
];

const modules = [
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'appointments', name: 'Appointments' },
  { id: 'staff', name: 'Staff Crew' },
  { id: 'services', name: 'Services Catalog' },
  { id: 'billing', name: 'Billing & Invoices' },
  { id: 'reports', name: 'Analytics & Reports' }
];

const AccessControl = () => {
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  
  // State for which stores this role can access
  const [roleStores, setRoleStores] = useState(['s1']); 
  
  // State for granular feature access: { moduleId: { view: true, create: false... } }
  const [permissions, setPermissions] = useState({
    dashboard: { view: true, create: false, edit: false, delete: false },
    appointments: { view: true, create: true, edit: true, delete: false },
    staff: { view: true, create: false, edit: false, delete: false },
    services: { view: true, create: false, edit: false, delete: false },
    billing: { view: true, create: true, edit: false, delete: false },
    reports: { view: false, create: false, edit: false, delete: false },
  });

  // Toggle Store Access
  const toggleStore = (storeId) => {
    setRoleStores(prev => 
      prev.includes(storeId) ? prev.filter(id => id !== storeId) : [...prev, storeId]
    );
  };

  // Toggle Feature Permission (View, Edit, Delete)
  const togglePermission = (moduleId, action) => {
    setPermissions(prev => ({
      ...prev,
      [moduleId]: {
        ...prev[moduleId],
        [action]: !prev[moduleId][action]
      }
    }));
  };

  return (
    <div className={styles.container}>
      
      <div className={styles.header}>
        <div>
          <h1 className={styles.pageTitle}>Access Control</h1>
          <p className={styles.pageSubtitle}>Manage roles, store access, and feature permissions.</p>
        </div>
        <button className={styles.saveBtn}>
          <Save size={16} />
          Save Changes
        </button>
      </div>

      <div className={styles.layout}>
        
        {/* Left Sidebar: Role Selection */}
        <div className={styles.roleSidebar}>
          <h3 className={styles.sectionTitle}>
            <Shield size={16} /> Select Role
          </h3>
          <div className={styles.roleList}>
            {roles.map(role => (
              <button 
                key={role}
                className={`${styles.roleBtn} ${selectedRole === role ? styles.activeRole : ''}`}
                onClick={() => setSelectedRole(role)}
              >
                {role}
              </button>
            ))}
          </div>
          <button className={styles.newRoleBtn}>+ Create New Role</button>
        </div>

        {/* Right Content: Store & Permission Configuration */}
        <div className={styles.configArea}>
          
          {/* Store Assignment */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                <Store size={18} className={styles.iconBlue} /> 
                Store Assignment for "{selectedRole}"
              </h3>
              <p className={styles.cardDesc}>Select which branches users with this role can access.</p>
            </div>
            
            <div className={styles.storeGrid}>
              {availableStores.map(store => {
                const isSelected = roleStores.includes(store.id);
                return (
                  <div 
                    key={store.id} 
                    className={`${styles.storeToggle} ${isSelected ? styles.storeActive : ''}`}
                    onClick={() => toggleStore(store.id)}
                  >
                    <div className={styles.checkbox}>
                      {isSelected && <Check size={14} strokeWidth={3} />}
                    </div>
                    <span>{store.name}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Granular Feature Permissions Matrix */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Page & Feature Permissions</h3>
              <p className={styles.cardDesc}>Define exactly what this role can see and do on each page.</p>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.matrixTable}>
                <thead>
                  <tr>
                    <th>Module / Page</th>
                    <th className={styles.centerAlign}>View</th>
                    <th className={styles.centerAlign}>Create</th>
                    <th className={styles.centerAlign}>Edit</th>
                    <th className={styles.centerAlign}>Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map(module => (
                    <tr key={module.id}>
                      <td className={styles.moduleName}>{module.name}</td>
                      {['view', 'create', 'edit', 'delete'].map(action => {
                        const isGranted = permissions[module.id]?.[action];
                        return (
                          <td key={action} className={styles.centerAlign}>
                            <button 
                              className={`${styles.toggleSwitch} ${isGranted ? styles.switchOn : styles.switchOff}`}
                              onClick={() => togglePermission(module.id, action)}
                            >
                              <div className={styles.switchHandle}>
                                {isGranted ? <Check size={10} className={styles.checkIcon}/> : <X size={10} className={styles.xIcon}/>}
                              </div>
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default AccessControl;